import tinycolor from "tinycolor2";
import type { ColorGroup, ColorToken } from "@/components/DesignTokens/types";
import { UNGROUPED } from "@/components/DesignTokens/store/designTokensStore";
import {
  formatCssVariableName,
  recursivelyResolveCssVariable,
} from "@/utils/iframeUtils";
export const COLOUR_FALLBACK = "#000000"; // black for colours we can't resolve

/**
 * Normalized color value interface for consistent handling
 */
export interface NormalizedColorValue {
  hex: string;
  original: string;
  isValid: boolean;
}

/**
 * Validates if a CSS value represents a valid color
 * Handles modern CSS syntax that tinycolor might not recognize directly
 */
export const isValidCssColor = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;

  const trimmedValue = value.trim();
  // Handle space-separated HSL/HSV syntax (e.g., "0 84.2% 60.2%") which is valid CSS but not recognized by tinycolor
  if (trimmedValue.includes("%") && !/^#|hsv|hsl|rgb|var/.test(trimmedValue)) {
    const parts = trimmedValue.split(/\s+/);

    if (parts.length >= 3) {
      const h = parts[0];
      const s = parts[1].endsWith("%") ? parts[1] : `${parts[1]}%`;
      const l = parts[2].endsWith("%") ? parts[2] : `${parts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const tc = tinycolor(tinycolorHsl);
      return tc.isValid();
    }
  }

  // For all other cases, use tinycolor directly
  const tc = tinycolor(trimmedValue);
  return tc.isValid();
};

const VALID_HEX_COLOR_PATTERN =
  /^#?([a-fA-F0-9]{3}|[a-fA-F0-9]{4}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/;

export const isValidHexColor = (value: string): boolean => {
  return VALID_HEX_COLOR_PATTERN.test(value);
};

/**
 * Parses RGB values with Tailwind opacity variables and converts to hex
 * e.g., "rgb(251 191 36 / var(--tw-bg-opacity))" -> "#fbbf24"
 */
const parseRgbWithTailwindOpacity = (value: string): string | null => {
  const rgbMatch = value.match(/rgb\(([^)]+)\)/);
  if (!rgbMatch) return null;

  const rgbContent = rgbMatch[1];
  const rgbPart = rgbContent.split("/")[0].trim();
  const rgbValues = rgbPart.split(/\s+/).map((v) => Number.parseInt(v.trim()));

  if (
    rgbValues.length >= 3 &&
    rgbValues.every((v) => !Number.isNaN(v) && v >= 0 && v <= 255)
  ) {
    const [r, g, b] = rgbValues;
    const color = tinycolor({ r, g, b });
    return color.toHexString();
  }

  return null;
};

/**
 * Normalizes any color value to hex format
 * Handles CSS functions, space-separated HSL, and converts valid colors to hex using TinyColor2
 */
export const normalizeColorValue = (value: string): string => {
  if (!value) return value;

  // If it already starts with #, return as-is
  if (value.startsWith("#")) {
    return value;
  }

  // For simple hex without #, add prefix
  if (VALID_HEX_COLOR_PATTERN.test(value)) {
    return `#${value}`;
  }

  // If it's a CSS function or custom property, handle special cases
  if (isCssColorFunction(value)) {
    // Special handling for modern CSS rgb syntax with Tailwind opacity variables
    // e.g., "rgb(251 191 36 / var(--tw-bg-opacity))" -> extract RGB and convert to hex
    if (value.includes("rgb(") && value.includes("/ var(--tw-")) {
      const hexValue = parseRgbWithTailwindOpacity(value);
      if (hexValue) return hexValue;
    }

    // For other CSS functions or custom properties, return as-is
    return value;
  }

  // If it's not a valid CSS color, return as-is
  if (!isValidCssColor(value)) return value;

  // Handle modern CSS space-separated HSL syntax before passing to tinycolor
  const trimmedValue = value.trim();
  if (
    trimmedValue.includes("%") &&
    !trimmedValue.startsWith("#") &&
    !trimmedValue.startsWith("hsv")
  ) {
    const parts = trimmedValue.split(/\s+/);
    if (parts.length >= 3) {
      const h = parts[0];
      const s = parts[1].endsWith("%") ? parts[1] : `${parts[1]}%`;
      const l = parts[2].endsWith("%") ? parts[2] : `${parts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const color = tinycolor(tinycolorHsl);
      return color.isValid() ? color.toHexString() : value;
    }
  }

  // Try to convert any other color format to hex using tinycolor
  const color = tinycolor(trimmedValue);
  return color.isValid() ? color.toHexString() : value;
};

/**
 * Extracts color value from Tailwind color class and converts to hex
 */
export const extractColorFromTailwindClass = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }>,
): string => {
  // Handle Tailwind classes like bg-red-500, text-blue-300, etc.
  if (
    value.startsWith("bg-") ||
    value.startsWith("text-") ||
    value.startsWith("border-") ||
    value.startsWith("from-") ||
    value.startsWith("to-") ||
    value.startsWith("via-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    // First, try to find the exact class in comboboxOptions
    let targetClass = value;
    let equivalentColor = comboboxOptions.find(
      (option) => option.tailwind === targetClass,
    );

    // If not found and it doesn't have a prefix, try to determine the correct prefix
    if (!equivalentColor && !value.match(/^(bg|text|border|from|to|via)-/)) {
      // Determine the correct prefix from comboboxOptions
      let prefix = "bg-"; // default fallback
      if (comboboxOptions.length > 0) {
        const firstOption = comboboxOptions[0];
        const match = firstOption.tailwind.match(/^([a-z-]+)-/);
        if (match) {
          prefix = `${match[1]}-`;
        }
      }

      targetClass = `${prefix}${value}`;
      equivalentColor = comboboxOptions.find(
        (option) => option.tailwind === targetClass,
      );
    }

    if (equivalentColor) {
      return extractColorFromCssProperty(equivalentColor.css);
    }
  }

  return value;
};

/**
 * Extracts color value from CSS property string like "background-color: #ff0000;"
 */
export const extractColorFromCssProperty = (css: string): string => {
  const colorValue = css
    .replace(/^(background-color|color|border-color):\s*/, "")
    .replace(/;$/, "")
    .trim();

  return normalizeColorValue(colorValue);
};

/**
 * Handles CSS custom properties (variables starting with --)
 */
export const extractColorFromCssVariable = (value: string): string => {
  if (value.startsWith("--")) {
    const cleanedValue = value.replace(/-/g, "");
    return normalizeColorValue(cleanedValue);
  }

  return value;
};

/**
 * Comprehensive color value parser that handles all supported formats
 */
export const parseColorValue = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }> = [],
): NormalizedColorValue => {
  if (!value) {
    return { hex: COLOUR_FALLBACK, original: value, isValid: false };
  }

  let processedValue = value;

  // Handle CSS custom properties
  if (value.startsWith("--")) {
    processedValue = extractColorFromCssVariable(value);
  }
  // Handle Tailwind classes
  else if (
    value.startsWith("bg-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    processedValue = extractColorFromTailwindClass(value, comboboxOptions);
  }
  // Handle CSS properties
  else if (value.includes(":")) {
    processedValue = extractColorFromCssProperty(value);
  }
  // Handle all other color formats with TinyColor2
  else {
    processedValue = normalizeColorValue(value);
  }

  // Special handling for CSS variables - don't validate with tinycolor since they can't be resolved
  if (value.startsWith("var(--") || processedValue.startsWith("var(--")) {
    return {
      hex: value, // Return the CSS variable as-is
      original: value,
      isValid: true, // Consider CSS variables as valid
    };
  }

  const color = tinycolor(processedValue);
  const isValid = color.isValid();

  let hex = COLOUR_FALLBACK;
  if (isValid) {
    hex = color.toHexString();

    // Convert #ffee11aa to #fe1a
    let dedupedString = "";
    for (let i = 1; i < hex.length; i += 2) {
      if (hex[i] === hex[i + 1]) {
        dedupedString += hex[i];
      } else {
        dedupedString = "";
        break;
      }
    }

    if (dedupedString) {
      hex = "#" + dedupedString;
    }
  }

  return {
    hex,
    original: value,
    isValid,
  };
};

/**
 * Removes gradient-specific CSS variables from color values
 */
export const cleanGradientVariables = (value: string): string => {
  return value
    .replace("var(--tw-gradient-to-position)", "")
    .replace("var(--tw-gradient-from-position)", "")
    .replace("var(--tw-gradient-via-position)", "")
    .trim();
};

/**
 * Safely extracts color value from CSS property string
 * Replaces duplicate safeExtractValueFromCss functions
 */
export const safeExtractValueFromCss = (css: string): string | null => {
  try {
    const firstCss = css.split("\n")[0];
    let value: string = firstCss.split(":")[1];
    value = value.split(";")[0];
    return value.trim();
  } catch (error) {
    return null;
  }
};

/**
 * Cleans a color value from various formats (Tailwind classes, CSS properties, etc.)
 * and returns a clean color value suitable for display in an input
 */
export const cleanBgColorValue = (value: string): string => {
  if (!value) return "";

  // Handle CSS property format first (most reliable)
  if (value.includes("background-color:")) {
    // Extract the background-color value
    const bgMatch = value.match(/background-color:\s*([^;]+)/);
    if (bgMatch) {
      const cssValue = bgMatch[1].trim();

      // Handle modern CSS color syntax with CSS variables
      // e.g., "rgb(34 197 94 / var(--tw-bg-opacity))" -> convert to hex
      if (
        cssValue.includes("rgb(") &&
        cssValue.includes("/ var(--tw-bg-opacity))")
      ) {
        const hexValue = parseRgbWithTailwindOpacity(cssValue);
        if (hexValue) return hexValue;
      }

      // For other CSS values, return the cleaned value
      return cssValue.replace(/;$/, "").trim();
    }
  }

  // Handle arbitrary Tailwind class format first (e.g., bg-[#ff0000])
  // This needs to be checked before splitting by spaces to handle spaces within brackets
  if (value.startsWith("bg-[") && value.endsWith("]")) {
    const match = value.match(/^bg-\[(.*)\]$/);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  // Handle multiple classes - process from LAST to FIRST for better fallback
  // Split and reverse to check most recent/specific classes first
  const classes = value.split(" ").filter(Boolean).reverse();

  // Try to find a valid color class, checking from last to first
  for (const cls of classes) {
    // Skip variant classes (like dark:, hover:, etc.)
    if (cls.includes(":")) continue;

    // Handle arbitrary Tailwind class format (e.g., bg-[#ff0000])
    if (cls.startsWith("bg-[") && cls.endsWith("]")) {
      // Use a more robust regex to extract content between bg-[ and ]
      const match = cls.match(/^bg-\[(.*)\]$/);
      if (match?.[1]) {
        return match[1].trim();
      }
    }
  }

  return value.trim();
};

/**
 * Extracts position value from CSS or Tailwind option
 * Replaces extractPositionValue function
 */
export const extractPositionValue = (option: {
  css?: string;
  tailwind?: string;
}): string => {
  if (option.css) {
    const match = option.css.match(/:([^;]+);/);
    if (match) return match[1].trim();
  }

  if (option.tailwind) {
    const match = option.tailwind.match(/-(\d+)%/);
    if (match) return `${match[1]}%`;
  }

  return "";
};

/**
 * CSS color function patterns that should be preserved as-is
 */
export const CSS_COLOR_PATTERNS = [
  "var(--",
  "hsl(",
  "hsv(",
  "rgb(",
  "rgba(",
  "oklch(",
  "hwb(",
  "lab(",
  "lch(",
] as const;

/**
 * Check if a color value is a CSS function or custom property
 */
export function isCssColorFunction(colorValue: string): boolean {
  return CSS_COLOR_PATTERNS.some((pattern) => colorValue.includes(pattern));
}

// Removed - using the more sophisticated version above at lines 45-68

/**
 * Convert hex color to space-separated RGB values for Tailwind CSS variables
 */
export function hexToRgbSpaceSeparated(hex: string): string {
  const cleanHex = hex.replace("#", "");
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);

  if (result) {
    const r = Number.parseInt(result[1], 16);
    const g = Number.parseInt(result[2], 16);
    const b = Number.parseInt(result[3], 16);
    return `${r} ${g} ${b}`;
  }

  return "0 0 0"; // fallback
}

/**
 * Get appropriate CSS value format for Tailwind classes
 */
export function getCssColorValue(colorValue: string): string {
  if (colorValue.startsWith("#")) {
    // Hex color - convert to rgb format
    return `rgb(${hexToRgbSpaceSeparated(colorValue)})`;
  }

  if (isCssColorFunction(colorValue)) {
    // CSS function or variable - use as-is
    return colorValue;
  }

  // Assume it's a hex color without #
  return `rgb(${hexToRgbSpaceSeparated(`#${colorValue}`)})`;
}

/**
 * Strip # prefix from hex colors for API requests (backend expects no #)
 */
export function stripHexPrefix(colorValue: string): string {
  return colorValue.startsWith("#") ? colorValue.slice(1) : colorValue;
}

/**
 * Resolve CSS custom property to actual color value
 */
export function resolveCssCustomProperty(
  colorValue: string,
  cssCustomProperties?: Record<string, string>,
): { resolvedValue: string; hexValue: string } {
  let resolvedValue = colorValue;
  let hexValue = colorValue;

  if (cssCustomProperties && colorValue.includes("var(--")) {
    const varMatch = colorValue.match(/var\(--([^)]+)\)/);
    if (varMatch) {
      const varName = `--${varMatch[1]}`;
      const cssValue = cssCustomProperties[varName];
      if (cssValue) {
        resolvedValue = cssValue;

        // Handle space-separated HSL values (e.g., "214.3 31.8% 91.4%")
        if (
          cssValue.includes("%") &&
          !cssValue.startsWith("#") &&
          !cssValue.includes("(")
        ) {
          const parts = cssValue.trim().split(/\s+/);
          if (parts.length >= 3) {
            const h = parts[0];
            const s = parts[1].endsWith("%") ? parts[1] : `${parts[1]}%`;
            const l = parts[2].endsWith("%") ? parts[2] : `${parts[2]}%`;
            const hslValue = `hsl(${h}, ${s}, ${l})`;

            // Use tinycolor to convert to hex
            try {
              const tc = tinycolor(hslValue);
              if (tc.isValid()) {
                hexValue = tc.toHexString();
              }
            } catch {
              // Fallback if tinycolor is not available
              hexValue = hslValue;
            }
          }
        } else {
          // Try to convert other CSS color formats to hex
          try {
            if (isValidCssColor(cssValue)) {
              const tc = tinycolor(cssValue);
              if (tc.isValid()) {
                hexValue = tc.toHexString();
              }
            }
          } catch {
            // Fallback if modules are not available
            hexValue = cssValue;
          }
        }
      }
    }
  }

  return { resolvedValue, hexValue };
}

// Shared utility functions
/**
 * Utility function to convert modern CSS space-separated HSL syntax to hex color
 */
export const parseSpaceSeparatedHsl = (cssValue: string): string | null => {
  if (
    cssValue.includes("%") &&
    !cssValue.startsWith("#") &&
    !cssValue.startsWith("hsv")
  ) {
    const hslParts = cssValue.split(/\s+/);
    if (hslParts.length >= 3) {
      const h = hslParts[0];
      const s = hslParts[1].endsWith("%") ? hslParts[1] : `${hslParts[1]}%`;
      const l = hslParts[2].endsWith("%") ? hslParts[2] : `${hslParts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const tc = tinycolor(tinycolorHsl);
      if (tc.isValid()) {
        return tc.toHexString();
      }
    }
  }
  return null;
};

/**
 * Gets the display color for a ColorToken with proper CSS variable resolution
 */
export const getDisplayColor = (
  color: ColorToken,
  cssCustomProperties?:
    | { root: Record<string, string>; dark: Record<string, string> }
    | null
    | Record<string, string>,
  comboboxOptions?: Array<{ tailwind: string; css: string }>,
): string => {
  // 1. First, try to use the direct color values (these are mode-aware)
  const value = color.color || color.hexColor || color.value;

  // 2. Handle CSS custom properties resolution
  if (cssCustomProperties && value.includes("var(--")) {
    let propsToUse: Record<string, string>;
    if (
      cssCustomProperties &&
      typeof cssCustomProperties === "object" &&
      "root" in cssCustomProperties &&
      "dark" in cssCustomProperties
    ) {
      const nestedProps = cssCustomProperties as {
        root: Record<string, string>;
        dark: Record<string, string>;
      };
      propsToUse = nestedProps.root || {};
    } else {
      propsToUse = {};
    }

    const resolvedValue = recursivelyResolveCssVariable(value, propsToUse);
    if (resolvedValue && resolvedValue !== value) {
      const tc = tinycolor(resolvedValue);
      if (tc.isValid()) {
        return tc.toHexString();
      }
    }
    // If the resolved value is the same as the original value, it means the CSS variable couldn't be resolved
    // This should be treated as an error, but we'll let the calling code handle that
  }

  // 3. Try to normalize the color value to get a hex code
  const normalizedColor = normalizeColorValue(value);

  // If normalizeColorValue returns a valid hex code, use it
  if (normalizedColor?.startsWith("#")) {
    return normalizedColor;
  }

  // 4. Try to convert any color format (including HSL) to hex using tinycolor
  const tc = tinycolor(value);
  if (tc.isValid()) {
    return tc.toHexString();
  }

  // 5. Try to parse space-separated HSL syntax (e.g., "210 40% 96.1%")
  const spaceSeparatedHex = parseSpaceSeparatedHsl(value);
  if (spaceSeparatedHex) {
    return spaceSeparatedHex;
  }

  // 6. ONLY fallback to comboboxOptions lookup if direct values failed
  // (This is where the bug was - comboboxOptions has stale cached values)
  if (comboboxOptions && comboboxOptions.length > 0) {
    // Determine the correct prefix from comboboxOptions
    let prefix = "bg-"; // default fallback
    const firstOption = comboboxOptions[0];
    const match = firstOption.tailwind.match(/^([a-z-]+)-/);
    if (match) {
      prefix = `${match[1]}-`;
    }

    const tailwindClass = `${prefix}${color.tailwindClass}`;
    const parsed = comboboxOptions.find(
      (option) => option.tailwind === tailwindClass,
    );
    if (parsed) {
      const tc = tinycolor(parsed.css);
      if (tc.isValid()) {
        return tc.toHexString();
      }
    }
  }

  // 7. If the value is still a CSS variable that couldn't be resolved, show formatted name
  if (value.includes("var(--")) {
    return formatCssVariableName(value);
  }

  // 8. Final fallback
  return normalizedColor || value;
};

/**
 * Groups colors based on meaningful group names. Colors with generic or empty group names
 * are moved to an "Ungrouped" section, while colors with specific group names (including
 * numeric names like "4", "primary", "brand", etc.) retain their group identity.
 */
export const groupColors = (colorGroups: ColorGroup[]): ColorGroup[] => {
  const meaningfulGroups: ColorGroup[] = [];
  const ungroupedColors: ColorToken[] = [];

  for (const group of colorGroups) {
    // Consider a group "meaningful" if it has a meaningful name
    // Unmeaningful names are typically generic, empty, or default values
    const isMeaningfulGroupName =
      group.name &&
      group.name.trim() !== "" &&
      ![
        "default",
        "colors",
        "color",
        "ungrouped",
        "other",
        "others",
        "misc",
      ].includes(group.name.toLowerCase());

    if (isMeaningfulGroupName) {
      meaningfulGroups.push(group);
    } else {
      ungroupedColors.push(...group.colors);
    }
  }

  // Sort ungrouped colors alphabetically
  ungroupedColors.sort((a, b) => a.name.localeCompare(b.name));

  // Put ungrouped colors at the top with a special marker
  const result: ColorGroup[] = [];
  if (ungroupedColors.length > 0) {
    result.push({
      name: UNGROUPED, // Special marker for ungrouped colors
      colors: ungroupedColors,
    });
  }
  result.push(...meaningfulGroups);

  return result;
};

/**
 * Filters color groups based on search query
 */
export const filterColorGroups = (
  colorGroups: ColorGroup[],
  searchQuery: string,
): ColorGroup[] => {
  if (!searchQuery.trim()) {
    return groupColors(colorGroups);
  }

  const query = searchQuery.toLowerCase();
  const filteredGroups = colorGroups
    .map((group) => {
      const matchingColors = group.colors.filter(
        (color) =>
          color.name.toLowerCase().includes(query) ||
          color.value.toLowerCase().includes(query) ||
          color.tailwindClass.toLowerCase().includes(query),
      );

      // Show group if it matches by name OR has matching colors
      const groupMatches = group.name.toLowerCase().includes(query);

      return {
        ...group,
        colors: groupMatches ? group.colors : matchingColors, // Show all colors if group name matches
      };
    })
    .filter(
      (group) =>
        group.name
          .toLowerCase()
          .includes(query) || // Show if group name matches
        group.colors.length > 0, // Or if it has matching colors
    );

  // Apply the same grouping logic for search results
  return groupColors(filteredGroups);
};

/**
 * Converts backend color data to frontend format
 */
export const convertBackendColorsToFrontend = (
  backendColorGroups: ColorGroup[],
  cssCustomProperties?: Record<string, string>,
): ColorGroup[] => {
  const convertedColorGroups: ColorGroup[] = backendColorGroups.map(
    (group) => ({
      name: group.name,
      colors: group.colors.map((color) => {
        // For colors with "DEFAULT" in the name, show "DEFAULT" as the display name
        const displayName = color.name.includes(" DEFAULT")
          ? "DEFAULT"
          : color.name;

        // Try to resolve the color value using CSS custom properties if available
        let resolvedValue = color.color;
        let hexValue = color.color;

        if (cssCustomProperties && color.color.includes("var(--")) {
          // Handle CSS variables
          const varMatch = color.color.match(/var\(--([^)]+)\)/);
          if (varMatch) {
            const varName = `--${varMatch[1]}`;
            const cssValue = cssCustomProperties[varName];
            if (cssValue && isValidCssColor(cssValue)) {
              resolvedValue = cssValue;
              const tc = tinycolor(cssValue);
              if (tc.isValid()) {
                hexValue = tc.toHexString();
              } else {
                // Handle modern CSS space-separated HSL syntax
                const parsedHex = parseSpaceSeparatedHsl(cssValue);
                if (parsedHex) {
                  hexValue = parsedHex;
                }
              }
            }
          }
        } else {
          // Handle direct color values (hex, hsl, rgb, etc.)
          const tc = tinycolor(color.color);
          if (tc.isValid()) {
            hexValue = tc.toHexString();
            resolvedValue = color.color;
          } else {
            // If color.color is not valid, try color.value (which might be hex without #)
            const valueWithHash = color.value.startsWith("#")
              ? color.value
              : `#${color.value}`;
            const tcValue = tinycolor(valueWithHash);
            if (tcValue.isValid()) {
              hexValue = tcValue.toHexString();
              resolvedValue = valueWithHash;
            } else {
              // Final fallback: ensure hex values have # prefix
              hexValue = color.color.startsWith("#")
                ? color.color
                : `#${color.color}`;
              resolvedValue = color.color;
            }
          }
        }

        return {
          name: displayName,
          group: group.name,
          value: color.color,
          color: hexValue,
          tailwindClass:
            group.name.toLowerCase() === color.name.toLowerCase()
              ? color.name.toLowerCase()
              : `${group.name.toLowerCase()}-${color.name}`,
          hexColor: hexValue,
          hasError: false,
        };
      }),
    }),
  );

  // Sort colors within each group (DEFAULT first)
  for (const group of convertedColorGroups) {
    group.colors.sort((a, b) => {
      if (a.name === "DEFAULT") return -1;
      if (b.name === "DEFAULT") return 1;
      return a.name.localeCompare(b.name);
    });
  }

  // Sort groups alphabetically
  convertedColorGroups.sort((a, b) => a.name.localeCompare(b.name));

  return convertedColorGroups;
};
